import React, { useState, useRef } from 'react';
import useGameStore from '../../store/gameStore';
import { useSound } from '../../hooks/useSound';
import AutoBetPanel from './AutoBetPanel';
import './BetPanel.css';

const DIFFICULTIES = [
  { id: 'easy',    label: '🟢 Easy',    color: '#39ff7a' },
  { id: 'medium',  label: '🟡 Medium',  color: '#f5c842' },
  { id: 'hard',    label: '🟠 Hard',    color: '#ff8c00' },
  { id: 'extreme', label: '🔴 Extreme', color: '#ff3b5c' },
];

const DIFFICULTY_ACTIVE_STYLE = Object.fromEntries(
  DIFFICULTIES.map(d => [d.id, { borderColor: d.color, color: d.color }])
);
const EMPTY_STYLE = {};

const QUICK_BETS = [
  { label: '0.1', action: () => 0.1 },
  { label: '1',   action: () => 1 },
  { label: '10',  action: () => 10 },
  { label: '50',  action: () => 50 },
  { label: '2X',  action: v => v * 2 },
  { label: 'Max', action: (_, bal, maxBet) => Math.floor(Math.min(bal, maxBet)) },
];

export default function BetPanel({ placeBet, cashOut, startAutoBet, stopAutoBet }) {
  const { play } = useSound();
  const {
    gameState, betAmount, setBetAmount, difficulty, setDifficulty,
    balance, autoBet, gameConfig,
  } = useGameStore();

  const minBet = gameConfig?.minBet ?? 0.1;
  const maxBet = gameConfig?.maxBet ?? Infinity;
  const betCap = Math.min(balance, maxBet);

  const [showAutoBet, setShowAutoBet] = useState(false);
  const [betInputRaw, setBetInputRaw] = useState(String(betAmount));

  const isIdle    = gameState === 'idle' || gameState === 'round_completed';
  const isActive  = gameState === 'round_started' || gameState === 'step_success';
  const isWaiting = gameState === 'waiting_for_server';
  const isEnded   = gameState === 'step_failed' || gameState === 'cashed_out';

  // Keep cashout visible during the brief waiting_for_server that follows takeStep/cashOut
  const wasActiveRef    = useRef(false);
  const cashoutFlightRef = useRef(false);
  if (isActive) wasActiveRef.current = true;
  if (isIdle || isEnded) { wasActiveRef.current = false; cashoutFlightRef.current = false; }
  if (!isWaiting) cashoutFlightRef.current = false; // clear once server responds
  const showCashout = isActive || (isWaiting && wasActiveRef.current);

  function handleBetInput(e) {
    const raw = e.target.value;
    if (raw === '') { setBetInputRaw(raw); setBetAmount(0); return; }
    const val = parseFloat(raw);
    if (isNaN(val) || val < 0) return;
    if (val > betCap) {
      const clamped = betCap;
      setBetInputRaw(String(clamped));
      setBetAmount(clamped);
      return;
    }
    setBetInputRaw(raw);
    setBetAmount(val);
  }

  function handleBetBlur() {
    if (betAmount < minBet) {
      setBetAmount(minBet);
      setBetInputRaw(String(minBet));
    }
  }

  function handleQuickBet(action) {
    play('click');
    const next = action(betAmount || 0, balance, maxBet);
    const clamped = Math.max(minBet, Math.min(next, betCap));
    setBetAmount(clamped);
    setBetInputRaw(String(clamped));
  }

  function handleStart() {
    if (betAmount < minBet || betAmount > betCap) return;
    placeBet(betAmount, difficulty);
  }

  function handleCashOut() {
    play('cashout');
    cashoutFlightRef.current = true;
    cashOut();
  }

  return (
    <div className="bet-panel">
      {/* Bet Amount */}
      <div className="panel-section">
        <label className="panel-label">Bet Amount</label>
        <div className="bet-input-row">
          <button
            type="button"
            className="bet-step"
            onClick={() => handleQuickBet(v => v - 1)}
            disabled={!isIdle && !isEnded}
            aria-label="Decrease bet"
          >
            −
          </button>
          <input
            className="bet-input"
            type="number"
            min={minBet}
            max={maxBet}
            step="0.1"
            value={betInputRaw}
            onChange={handleBetInput}
            onBlur={handleBetBlur}
            disabled={!isIdle && !isEnded}
          />
          <button
            type="button"
            className="bet-step"
            onClick={() => handleQuickBet(v => v + 1)}
            disabled={!isIdle && !isEnded}
            aria-label="Increase bet"
          >
            +
          </button>
        </div>
        <div className="quick-bet-grid">
          {QUICK_BETS.map(q => (
            <button
              key={q.label}
              className="quick-btn"
              onClick={() => handleQuickBet(q.action)}
              disabled={!isIdle && !isEnded}
            >
              {q.label}
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty */}
      <div className="panel-section">
        <label className="panel-label">Risk Level</label>
        <div className="difficulty-grid">
          {DIFFICULTIES.map(d => (
            <button
              key={d.id}
              className={`diff-btn${difficulty === d.id ? ' active' : ''}`}
              style={difficulty === d.id ? DIFFICULTY_ACTIVE_STYLE[d.id] : EMPTY_STYLE}
              onClick={() => { play('click'); setDifficulty(d.id); }}
              disabled={!isIdle && !isEnded}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>


      {/* Action buttons */}
      <div className="action-btns">
        <div className="btn-swap-wrap">
          <button
            className={`btn-start${showCashout ? ' btn-swap-hide' : ''}`}
            onClick={handleStart}
            disabled={isWaiting || autoBet || betAmount < minBet || betAmount > betCap || showCashout}
          >
            {autoBet ? '⚙ Auto Bet Running…' : '🚀 START GAME'}
          </button>
          <button
            className={`btn-cashout${!showCashout ? ' btn-swap-hide' : ''}`}
            onClick={handleCashOut}
            disabled={cashoutFlightRef.current}
          >
            💰 CASH OUT
          </button>
        </div>
      </div>

      {/* Auto Bet toggle */}
      <div className="panel-section">
        <button
          className={`auto-bet-toggle${showAutoBet ? ' open' : ''}`}
          onClick={() => { play('click'); setShowAutoBet(s => !s); }}
        >
          🤖 Auto Bet {showAutoBet ? '▲' : '▼'}
        </button>
        {showAutoBet && (
          <AutoBetPanel
            startAutoBet={startAutoBet}
            stopAutoBet={stopAutoBet}
          />
        )}
      </div>
    </div>
  );
}
